/* eslint-disable no-unused-vars,react/jsx-no-bind,react/no-unstable-nested-components */
import React from 'react';
import moment from 'moment-timezone';
import { useDispatch, useSelector } from 'react-redux';
import ReactPagination from 'react-js-pagination';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faCheck } from '@fortawesome/pro-duotone-svg-icons';
import { DateTime } from 'luxon';

// components
import { Table } from '../../../../common/components';

// actions + utils
import formatDispositionNumToString from '../../../../utils/format-disposition';

function Pagination({ handleLoadShoppers }) {
  const {
    page,
    pageSize,
    totalShoppers,
    totalPages,
  } = useSelector(store => store.shoppers);

  // pagination calculations
  const pageCountStart = ((page - 1) * pageSize) + 1;
  let pageCountEnd = page * pageSize;
  if (totalPages === page) pageCountEnd = totalShoppers;

  return (
    <div className="row">
      <div className="d-flex col justify-content-center">
        <div className="d-flex float-start align-items-end text-muted">
          <i>{`${pageCountStart}-${pageCountEnd} of ${totalShoppers}`}</i>
        </div>
        <ReactPagination
          activePage={page}
          itemsCountPerPage={pageSize}
          totalItemsCount={totalShoppers}
          pageRangeDisplayed={5}
          innerClass="pagination mb-0 ms-auto me-auto"
          itemClass="page-item"
          linkClass="page-link"
          onChange={(pageNum) => handleLoadShoppers({ page: pageNum })}
        />
      </div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="row">
      <div className="d-flex col fw-bold justify-content-end">
        {label || ''}
      </div>
      <div className="col">
        {value || ''}
      </div>
    </div>
  );
}

function AppointmentTable({ appointment }) {
  const {
    SOA_URL: soaURL,
    SOA_timestamp: soaTimestamp,
    appointment_duration: duration,
    appointment_location: location,
    appointment_meetingURL: meetingURL,
    appointment_start_time: startTime,
    appointment_type: type,
  } = (appointment || {});

  return (
    <>
      <Row label="Type" value={type} />
      <Row label="SOA URL" value={soaURL} />
      <Row label="SOA Timestamp" value={soaTimestamp} />
      <Row label="Duration" value={duration} />
      <Row label="Meeting URL" value={meetingURL} />
      <Row label="Meeting Time" value={DateTime.fromISO(startTime).toLocaleString(DateTime.DATETIME_FULL)} />
      <Row label="Meeting Timestamp" value={startTime} />
    </>
  );
}

function MeetingSubComponent({ data, isOpen }) {
  const {
    data: {
      appointment,
      original_appointment: originalAppointment,
    } = {},
  } = (data || {});
  return (
    <div className="row py-3">
      <div className="col">
        <div className="row justify-content-center fs-4 fw-bold mb-3">
          Original Appointment Information
        </div>
        {originalAppointment ? (
          <AppointmentTable appointment={originalAppointment} />
        ) : (
          <div className="row justify-content-center fst-italic">
            No Appointment Information
          </div>
        )}
      </div>

      <div className="col">
        <div className="row justify-content-center fs-4 fw-bold mb-3">
          Agent Updated Appointment Information
        </div>
        {appointment ? (
          <AppointmentTable appointment={appointment} />
        ) : (
          <div className="row justify-content-center fst-italic">
            Appointment hasn&apos;t been updated by the agent
          </div>
        )}
      </div>
    </div>
  );
}

function LeadsTable({ handleLoadShoppers, loading }) {
  const dispatch = useDispatch();
  const {
    shopperList,
    sortDirection,
    sortTerm,
  } = useSelector(store => store.shoppers);
  const { appointmentsProfile: appointmentsProfileFeature } = useSelector(store => store.theme.features);

  // formatters
  const formatName = (data) => `${data.first_name} ${data.last_name}`;
  const formatDate = (date) => moment.tz(date, moment.tz.guess()).format('MM/DD/YY HH:mm z');

  function formatBooleanCell(active) {
    const yes = <FontAwesomeIcon className="text-success" icon={faCheck} />;
    const no = <FontAwesomeIcon className="text-danger" icon={faTimes} />;
    return <span className="d-flex justify-content-center">{active ? yes : no}</span>;
  }

  function formatMeetingCell({ data, isOpen }) {
    const { appointment, original_appointment: originalAppointment } = data;
    const hasData = Boolean(appointment || originalAppointment);
    return hasData ? (
      <button
        type="button"
        className={`btn btn-link fs-6 p-0 ${isOpen ? 'fw-bold' : ''}`}
      >
        {isOpen ? 'Viewing' : 'View'}
      </button>
    ) : (
      <span className="text-muted fst-italic">None</span>
    );
  }

  function formatSubTableLink(data, view) {
    const handleClick = () => {
      const payload = {
        selectedShopper: data,
        view,
      };
      dispatch({ type: 'SET_SHOPPERS_KEYS', payload });
    };

    if ((view === 'soas') && (Number(data.soa_count) < 1)) {
      return (<i className="text-muted">None</i>);
    }
    if ((view === 'enrollments') && (Number(data.enrollment_count) < 1)) {
      return <i className="text-muted">None</i>;
    }

    return (
      <button
        type="button"
        className="btn btn-link fs-6 p-0"
        onClick={handleClick}
      >
        View
      </button>
    );
  }

  const schema = {
    cols: [
      {
        field: 'lead_id',
        header: 'Lead ID',
      },
      {
        header: 'Name',
        passAllData: true,
        formatter: formatName,
        alphaSortIcon: true,
        sortKey: 'last_name',
      },
      {
        field: 'lead_status',
        header: 'Disposition',
        formatter: formatDispositionNumToString,
      },
      {
        field: 'lead_type',
        header: 'Lead Type',
      },
      {
        field: 'lead_opened',
        header: 'Lead Opened',
        formatter: formatBooleanCell,
      },
      {
        field: 'created',
        header: 'Created',
        formatter: formatDate,
        sortKey: 'created',
      },
      (appointmentsProfileFeature && {
        field: 'data',
        header: 'Meeting Info',
        formatter: (data, conditionalOutput, isOpen) => formatMeetingCell({ data, isOpen }),
        openSubComponent: true,
        subComponentID: 'meeting',
        subComponentIcon: () => null,
      }),
      {
        header: 'SOA',
        passAllData: true,
        formatter: (data) => formatSubTableLink(data, 'soas'),
      },
      {
        header: 'Enrollment',
        passAllData: true,
        formatter: (data) => formatSubTableLink(data, 'enrollments'),
      },
    ],
    subComponent: (data) => (<MeetingSubComponent data={data} />),
    sortClickCallback: (key, direction) => handleLoadShoppers({ sortTerm: key, sortDirection: direction }),
    sortKeySetter: (sk) => dispatch({ type: 'SET_SHOPPERS_KEYS', payload: { sortTerm: sk } }),
    sortKeyGetter: () => sortTerm,
    sortDirectionSetter: (sd) => dispatch({ type: 'SET_SHOPPERS_KEYS', payload: { sortDirection: sd } }),
    sortDirectionGetter: () => sortDirection,
  };

  return (
    <>
      <div className="row">
        <Table
          className="table table-striped"
          schema={schema}
          data={shopperList}
          loading={loading}
        />
      </div>

      <Pagination handleLoadShoppers={handleLoadShoppers} />
    </>
  );
}

export default LeadsTable;
